import { AsyncPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import axios, { AxiosError } from 'axios';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  RunTransitionWithDialog,
  WorkflowService,
} from 'src/app/shared/services/workflow/workflow.service';
import { DiscardCaseComponent } from 'src/app/shared/workflow-dialogs/discard-case/discard-case.component';
import { environment } from 'src/environments/environment';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import {
  DialogData,
  DownloadCaseDocumentsDialogComponent,
} from '../download-case-documents-dialog/download-case-documents-dialog.component';
import { PayeeCasesService } from '../payee-cases.service';
import {
  StartPaymentChoiceDialogComponent,
  StartPaymentChoiceDialogData,
} from '../start-payment-choice-dialog/start-payment-choice-dialog.component';
import {
  CaseTableItem,
  CaseTableItemAction,
  CaseTableItemActionGroup,
} from '../types';

export type AdditionalAction<T = CaseTableItem> = {
  label: Observable<string>;
  show: (element: T) => boolean;
  onClick: (element: T) => void;
  icon?: string;
};

@Component({
  selector: 'payee-cases-actions-menu',
  templateUrl: './cases-actions-menu.component.html',
  styleUrls: ['./cases-actions-menu.component.scss'],
  imports: [
    PayeeIconButtonComponent,
    PayeeIconComponent,
    MatMenuTrigger,
    MatMenu,
    PayeeFormFieldComponent,
    FormsModule,
    MatMenuItem,
    AsyncPipe,
    TranslateModule,
    MatIcon,
  ],
})
export class CasesActionsMenuComponent implements OnDestroy {
  private snackbar = inject(MatSnackBar);
  private workflowService = inject(WorkflowService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private payeeCasesService = inject(PayeeCasesService);

  private readonly destroy = new Subject<void>();

  readonly actionFilter = signal('');
  private menuIsOpen = false;

  @Input({ required: true }) element!: CaseTableItem;
  @Input() allowEdit = true;
  @Input() allowDelete = true;
  @Input() additionalActions: AdditionalAction[] = [];
  @Input() transitionMedia?: RunTransitionWithDialog['media'];
  @Input() transitionExtraParams?: RunTransitionWithDialog['extraParams'];

  @Output() menuOpened = new EventEmitter<void>();
  @Output() menuClosed = new EventEmitter<void>();

  @ViewChild('actionFilterInput')
  actionFilterInput?: ElementRef<HTMLInputElement>;

  constructor() {
    effect(() => {
      const filter = this.actionFilter();
      if (!this.menuIsOpen || !filter) {
        return;
      }

      // MatMenu steals focus when filtered menu items are added/removed.
      setTimeout(() => this.actionFilterInput?.nativeElement.focus(), 200);
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  onActionsMenuOpen(): void {
    this.menuIsOpen = true;
    this.actionFilter.set('');
    this.menuOpened.emit();

    setTimeout(() => this.actionFilterInput?.nativeElement?.focus(), 200);
  }

  onActionsMenuClosed(): void {
    this.menuIsOpen = false;
    this.actionFilter.set('');
    this.menuClosed.emit();
  }

  shouldShowActionFilter(): boolean {
    return this.getTotalActionsCount() > 5;
  }

  hasAdditionalActions(): boolean {
    return this.additionalActions.some((action) => action.show(this.element));
  }

  getTotalActionsCount(): number {
    const workflowActions = this.element.actions.reduce(
      (total, group) => total + group.items.length,
      0,
    );

    const localMenuItems =
      (this.element.is_editable && this.allowEdit ? 1 : 0) +
      (this.element.is_deletable && this.allowDelete ? 1 : 0) +
      (this.element.bailiff_contact_url ? 1 : 0) +
      (this.element.awaiting_start_payment ? 1 : 0) +
      1;

    const additionalMenuItems = this.additionalActions.filter((action) =>
      action.show(this.element),
    ).length;

    return workflowActions + localMenuItems + additionalMenuItems;
  }

  showEditAction(): boolean {
    return (
      this.element.is_editable &&
      this.allowEdit &&
      this.showAction(
        this.translate.instant('CASES_TABLE.CASE_ACTIONS.EDIT'),
      )
    );
  }

  showDeleteAction(): boolean {
    return (
      this.element.is_deletable &&
      this.allowDelete &&
      this.showAction(
        this.translate.instant('CASES_TABLE.CASE_ACTIONS.DELETE'),
      )
    );
  }

  showBailiffContactAction(): boolean {
    return (
      !!this.element.bailiff_contact_url &&
      this.showAction(
        this.translate.instant('CASES_TABLE.CASE_ACTIONS.BAILIFF_CONTACT'),
      )
    );
  }

  showDownloadDocumentsAction(): boolean {
    return this.showAction(
      this.translate.instant('CASES_TABLE.CASE_ACTIONS.DOWNLOAD_DOCUMENTS'),
    );
  }

  showPayFeeAction(): boolean {
    return (
      this.element.awaiting_start_payment &&
      this.showAction(
        this.translate.instant('CASES_TABLE.START_PAYMENT_CHOICE.TITLE'),
      )
    );
  }

  hasVisibleGroupItems(group: CaseTableItemActionGroup): boolean {
    return group.items.some((item) => this.showAction(item.label));
  }

  getRegularGroupItems(
    group: CaseTableItemActionGroup,
  ): CaseTableItemAction[] {
    return group.items.filter((item) => !item.is_close);
  }

  getCloseGroupItems(group: CaseTableItemActionGroup): CaseTableItemAction[] {
    return group.items.filter((item) => item.is_close);
  }

  showGroup(group: CaseTableItemActionGroup): boolean {
    if (this.hasVisibleGroupItems(group)) {
      return true;
    }

    if (group.id === 'operation') {
      return (
        this.showEditAction() ||
        this.showDownloadDocumentsAction() ||
        this.showDeleteAction() ||
        this.showBailiffContactAction() ||
        this.showPayFeeAction() ||
        this.additionalActions.some((action) => action.show(this.element))
      );
    }

    return false;
  }

  async copyCaseIdToClipboard(event: MouseEvent): Promise<void> {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(
        this.element.payee_case_reference_id,
      );

      this.snackbar.open(
        this.translate.instant('CASES_TABLE.COPY_SUCCESS'),
        this.translate.instant('OK'),
        { duration: 2000 },
      );
    } catch (error) {
      console.error('Failed to copy to clipboard', error);
    }
  }

  editCase(): void {
    this.router.navigateByUrl(`/case/${this.element.id}`);
  }

  openDownloadDocumentsDialog(): void {
    this.dialog.open<DownloadCaseDocumentsDialogComponent, DialogData>(
      DownloadCaseDocumentsDialogComponent,
      {
        data: {
          case_uuid: this.element.id,
          payee_case_reference_id: this.element.payee_case_reference_id,
        },
      },
    );
  }

  deleteCase(): void {
    const ref = this.dialog.open(DiscardCaseComponent, {
      data: { id: this.element.id },
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy))
      .subscribe(async (result: boolean) => {
        if (!result) {
          return;
        }

        try {
          const url =
            environment.baseUrl + `/api/payee-case/${this.element.id}`;
          await axios.delete(url);
          this.element.deleted_at = new Date().toDateString();

          if (this.payeeCasesService.payee_case_reference_id.value) {
            this.payeeCasesService.payee_case_reference_id.next(null);
          } else {
            await this.payeeCasesService.setCases();
          }
        } catch (error: AxiosError | unknown) {
          if (error instanceof AxiosError) {
            console.error(
              'Network error while deleting case',
              error.response?.data,
            );
          } else {
            console.error('Unknown error while deleting case', error);
          }
        }
      });
  }

  showAction(label: unknown): boolean {
    if (typeof label === 'string') {
      return label.toLowerCase().includes(this.actionFilter().toLowerCase());
    }
    return false;
  }

  openPayFeeDialog(): void {
    this.dialog.open<
      StartPaymentChoiceDialogComponent,
      StartPaymentChoiceDialogData
    >(StartPaymentChoiceDialogComponent, {
      data: {
        caseId: this.element.id,
        canPayWithCredit: this.element.can_pay_with_credit,
      },
      autoFocus: false,
    });
  }

  openTransitionDialog(event: CaseTableItemAction): void {
    if (!event.dialog) {
      return;
    }

    this.workflowService.openTransitionDialog({
      transitionId: event.id,
      caseId: this.element.id,
      dialog: event.dialog,
      media: this.transitionMedia,
      extraParams: this.transitionExtraParams,
    });
  }
}
